import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
} from "react";
import { useAuth } from "./AuthContext";
import { useUserRole } from "./UserRoleContext";
import { get } from "../services/api";
import { formatDateForInput } from "../utils/calendarDate";
import {
  parseCivilDateOnly,
  civilDateTodayInZone,
  normalizeBarTimeZone,
} from "../utils/civilDateTime";

const ArtistContext = createContext();

export const useArtist = () => {
  const context = useContext(ArtistContext);
  if (!context) {
    throw new Error("useArtist must be used within an ArtistProvider");
  }
  return context;
};

export const ArtistProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const { userRole } = useUserRole();
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [userArtists, setUserArtists] = useState([]);
  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showsLoading, setShowsLoading] = useState(false);
  const [showsError, setShowsError] = useState(null);
  // Groups screen cache: bars (artists use userArtists)
  const [groupsBars, setGroupsBars] = useState([]);
  const [groupsBarsLoading, setGroupsBarsLoading] = useState(false);
  const [groupsBarsError, setGroupsBarsError] = useState(null);
  const [groupsBarsFetched, setGroupsBarsFetched] = useState(false);
  // Group modal members cache: { [artistId]: { members, loading, error, hasFetched } }
  const [groupModalMembersCache, setGroupModalMembersCache] = useState({});

  // Fetch all artists for user
  const fetchArtists = useCallback(async () => {
    if (!currentUser || userRole !== "artist") {
      setUserArtists([]);
      setSelectedArtist(null);
      setShows([]);
      setLoading(false);
      setShowsError(null);
      setShowsLoading(false);
      setGroupsBars([]);
      setGroupsBarsFetched(false);
      setGroupsBarsLoading(false);
      setGroupsBarsError(null);
      setGroupModalMembersCache({});
      return;
    }

    try {
      setLoading(true);
      const data = await get("/artists/my-artists");

      if (data.success && data.artists) {
        setUserArtists(data.artists);
        setError(null);

        // If only one artist exists, automatically select it
        // This ensures availability and other features work correctly
        if (data.artists.length === 1) {
          setSelectedArtist(data.artists[0]);
          localStorage.setItem(
            "selectedArtistId",
            data.artists[0].artist_id.toString(),
          );
        } else {
          // Multiple artists: default to "all" mode (null) - only restore specific artist if saved
          const savedArtistId = localStorage.getItem("selectedArtistId");

          if (savedArtistId && savedArtistId !== "all") {
            const savedArtist = data.artists.find(
              (artist) => artist.artist_id === parseInt(savedArtistId),
            );
            if (savedArtist) {
              setSelectedArtist(savedArtist);
            } else {
              // Saved artist doesn't exist, default to "all"
              setSelectedArtist(null);
              localStorage.setItem("selectedArtistId", "all");
            }
          } else {
            // Default to "all" mode (null represents all artists)
            setSelectedArtist(null);
            localStorage.setItem("selectedArtistId", "all");
          }
        }
      } else {
        setError(data.error || "Failed to fetch artist profiles");
        setUserArtists([]);
        setSelectedArtist(null);
        setShows([]);
      }
    } catch (err) {
      console.error("Error fetching artist profiles:", err);
      setError(err.message);
      setUserArtists([]);
      setSelectedArtist(null);
      setShows([]);
    } finally {
      setLoading(false);
    }
  }, [currentUser, userRole]);

  // Fetch selected artist profile (for compatibility)
  const fetchArtistProfile = useCallback(async () => {
    if (!selectedArtist) {
      return;
    }

    try {
      const data = await get(
        `/artists/me?artistId=${selectedArtist.artist_id}`,
      );

      if (data.success && data.artist) {
        // Update selected artist with latest data
        setSelectedArtist(data.artist);
        // Also update in userArtists array
        setUserArtists((prev) =>
          prev.map((a) =>
            a.artist_id === data.artist.artist_id ? data.artist : a,
          ),
        );
        setError(null);
      } else {
        setError(data.error || "Failed to fetch artist profile");
      }
    } catch (err) {
      console.error("Error fetching artist profile:", err);
      setError(err.message);
    }
  }, [selectedArtist]);

  // Fetch all shows for selected artist(s)
  // When selectedArtist is null, fetch shows for all user artists
  const fetchShows = useCallback(async () => {
    if (!currentUser || userRole !== "artist") {
      setShows([]);
      setShowsError(null);
      setShowsLoading(false);
      return;
    }

    try {
      setShowsLoading(true);
      setShowsError(null);

      // Build query params - only include artistId if a specific artist is selected
      const params = new URLSearchParams();
      if (selectedArtist?.artist_id) {
        params.append("artistId", selectedArtist.artist_id);
      }
      // If selectedArtist is null, don't pass artistId - API will return all artists' shows

      const data = await get(`/shows/me?${params.toString()}`);

      if (data.success) {
        setShows(data.shows || []);
      } else {
        setShowsError(data.error || "Failed to fetch shows");
        setShows([]);
      }
    } catch (err) {
      console.error("Error fetching artist shows:", err);
      setShowsError(err.message);
      setShows([]);
    } finally {
      setShowsLoading(false);
    }
  }, [currentUser, userRole, selectedArtist?.artist_id]);

  // Refresh artists list
  const refreshArtists = useCallback(() => {
    fetchArtists();
  }, [fetchArtists]);

  // Refresh artist profile (for compatibility)
  const refreshArtistProfile = useCallback(() => {
    fetchArtistProfile();
  }, [fetchArtistProfile]);

  // Update selected artist and persist to localStorage
  // null = "all" artists, specific artist object = single artist
  const updateSelectedArtist = useCallback((artist) => {
    setSelectedArtist(artist);
    if (artist) {
      localStorage.setItem("selectedArtistId", artist.artist_id.toString());
    } else {
      localStorage.setItem("selectedArtistId", "all");
    }
  }, []);

  // Refresh shows
  const refreshShows = useCallback(() => {
    fetchShows();
  }, [fetchShows]);

  // Fetch bars for Groups screen (cached; skip if already fetched unless force)
  const fetchGroupsBars = useCallback(
    async (force = false) => {
      if (!currentUser || userRole !== "artist") return;
      if (!force && groupsBarsFetched) return;

      try {
        setGroupsBarsLoading(true);
        setGroupsBarsError(null);
        const data = await get("/artists/my-bars");

        if (data.success) {
          setGroupsBars(data.bars || []);
          setGroupsBarsFetched(true);
        } else {
          setGroupsBarsError(data.error || "Failed to load bars");
          setGroupsBars([]);
        }
      } catch (err) {
        console.error("Error fetching groups bars:", err);
        setGroupsBarsError(err.message || "Failed to load bars");
        setGroupsBars([]);
      } finally {
        setGroupsBarsLoading(false);
      }
    },
    [currentUser, userRole, groupsBarsFetched],
  );

  const refreshGroupsBars = useCallback(() => {
    return fetchGroupsBars(true);
  }, [fetchGroupsBars]);

  // Group modal members cache (Groups artist profile modal)
  const getGroupModalMembers = useCallback(
    (artistId) => {
      if (!artistId) {
        return { members: [], loading: false, error: null, hasFetched: false };
      }
      const cached = groupModalMembersCache[artistId];
      if (!cached) {
        return { members: [], loading: false, error: null, hasFetched: false };
      }
      return {
        members: cached.members ?? [],
        loading: !!cached.loading,
        error: cached.error ?? null,
        hasFetched: !!cached.hasFetched,
      };
    },
    [groupModalMembersCache],
  );

  const fetchGroupModalMembers = useCallback(
    async (artistId, force = false) => {
      if (!currentUser || userRole !== "artist" || !artistId) return null;
      const cached = groupModalMembersCache[artistId];
      if (!force && cached?.hasFetched) return cached.members ?? null;

      setGroupModalMembersCache((prev) => ({
        ...prev,
        [artistId]: {
          ...prev[artistId],
          loading: true,
          error: null,
        },
      }));

      try {
        const response = await get(`/artists/${artistId}/users`);
        if (response.success && response.users) {
          setGroupModalMembersCache((prev) => ({
            ...prev,
            [artistId]: {
              members: response.users,
              loading: false,
              error: null,
              hasFetched: true,
            },
          }));
          return response.users;
        }
        const errMsg = response.error || "Failed to load members";
        setGroupModalMembersCache((prev) => ({
          ...prev,
          [artistId]: {
            members: [],
            loading: false,
            error: errMsg,
            hasFetched: false,
          },
        }));
        return null;
      } catch (err) {
        console.error("Error fetching group modal members:", err);
        setGroupModalMembersCache((prev) => ({
          ...prev,
          [artistId]: {
            members: [],
            loading: false,
            error: err.message || "Failed to load members",
            hasFetched: false,
          },
        }));
        return null;
      }
    },
    [currentUser, userRole, groupModalMembersCache],
  );

  const invalidateGroupModalMembers = useCallback((artistId) => {
    if (!artistId) return;
    setGroupModalMembersCache((prev) => {
      const next = { ...prev };
      delete next[artistId];
      return next;
    });
  }, []);

  const refreshGroupModalMembers = useCallback(
    async (artistId) => {
      invalidateGroupModalMembers(artistId);
      return fetchGroupModalMembers(artistId, true);
    },
    [invalidateGroupModalMembers, fetchGroupModalMembers],
  );

  // Helper to filter shows by date range
  const getShowsByDateRange = (startDate, endDate) => {
    if (!shows || shows.length === 0) return [];
    const startYmd = formatDateForInput(startDate);
    const endYmd = formatDateForInput(endDate);
    if (!startYmd || !endYmd) return [];
    return shows.filter((show) => {
      const ymd = parseCivilDateOnly(formatDateForInput(show.date));
      return ymd && ymd >= startYmd && ymd <= endYmd;
    });
  };

  // Helper to get past shows
  const getPastShows = () => {
    if (!shows || shows.length === 0) return [];
    return shows.filter((show) => {
      const ymd = parseCivilDateOnly(formatDateForInput(show.date));
      if (!ymd) return false;
      const todayYmd = civilDateTodayInZone(
        normalizeBarTimeZone(show.bar_time_zone),
      );
      return ymd < todayYmd;
    });
  };

  // Helper to get upcoming shows
  const getUpcomingShows = () => {
    if (!shows || shows.length === 0) return [];
    return shows.filter((show) => {
      const ymd = parseCivilDateOnly(formatDateForInput(show.date));
      if (!ymd) return false;
      const todayYmd = civilDateTodayInZone(
        normalizeBarTimeZone(show.bar_time_zone),
      );
      return ymd >= todayYmd;
    });
  };

  // Helper to get shows for a specific date
  const getShowsForDate = (date) => {
    if (!shows || shows.length === 0) return [];

    const targetDateStr = formatDateForInput(
      date instanceof Date ? date : typeof date === "string" ? date : new Date(date),
    );

    return shows.filter((show) => formatDateForInput(show.date) === targetDateStr);
  };

  // Fetch artists on mount or when user/role changes
  useEffect(() => {
    fetchArtists();
  }, [fetchArtists]);

  useEffect(() => {
    const onArtistRelationships = () => {
      refreshGroupsBars();
      refreshArtists();
    };
    window.addEventListener("artistRelationshipsUpdated", onArtistRelationships);
    return () =>
      window.removeEventListener(
        "artistRelationshipsUpdated",
        onArtistRelationships,
      );
  }, [refreshGroupsBars, refreshArtists]);

  // Fetch shows when selected artist changes (including when null = "all")
  useEffect(() => {
    fetchShows();
  }, [fetchShows]);

  const value = {
    // New API
    selectedArtist,
    setSelectedArtist: updateSelectedArtist,
    userArtists,
    refreshArtists,
    // Legacy API (for compatibility during migration)
    artistProfile: selectedArtist,
    refreshArtistProfile,
    // Shows
    shows,
    showsLoading,
    showsError,
    refreshShows,
    // Groups screen cache (artists = userArtists, bars below)
    groupsBars,
    groupsBarsLoading,
    groupsBarsError,
    fetchGroupsBars,
    refreshGroupsBars,
    // Group modal members cache
    getGroupModalMembers,
    fetchGroupModalMembers,
    invalidateGroupModalMembers,
    refreshGroupModalMembers,
    // State
    loading,
    error,
    // Helper functions
    getShowsByDateRange,
    getPastShows,
    getUpcomingShows,
    getShowsForDate,
    // Helper to get all user artists
    getAllUserArtists: () => userArtists,
  };

  return (
    <ArtistContext.Provider value={value}>{children}</ArtistContext.Provider>
  );
};
